import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, GlobeIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { BVLogo } from './BVLogo';
type Page = 'home' | 'about' | 'contact' | 'business';
interface NavbarProps {
  currentPage: Page;
  onNavigate: (page: Page) => void;
}
export function Navbar({
  currentPage,
  onNavigate
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleNavClick = (page: Page, sectionId?: string) => {
    onNavigate(page);
    setIsMobileMenuOpen(false);
    if (sectionId) {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'th' : 'en');
  };
  const navBackground = isScrolled ? 'bg-white/90 backdrop-blur-xl border-b border-[#c9a961]/20 shadow-sm' : 'bg-transparent border-transparent';
  const textColor = isScrolled ? 'text-[#1d1d1f]' : 'text-white';
  const linkColor = isScrolled ? 'text-[#86868b] hover:text-[#c9a961]' : 'text-white/80 hover:text-white';
  const activeLinkColor = isScrolled ? 'text-[#1d1d1f]' : 'text-white';
  const reserveBtnClass = isScrolled ? 'bg-[#c9a961] text-black hover:bg-[#d4b872]' : 'bg-[#c9a961] text-black hover:bg-[#d4b872] md:bg-transparent md:border md:border-[#c9a961] md:text-[#c9a961] md:hover:bg-[#c9a961] md:hover:text-black';
  return <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBackground}`} data-id="element-208">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between" data-id="element-209">
          {/* Logo */}
          <button onClick={() => handleNavClick('home')} className="transition-all duration-300 flex items-center gap-3" data-id="element-210">
            <BVLogo size={isScrolled ? 44 : 48} darkBackground={!isScrolled} data-id="element-211" />
          </button>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-8" data-id="element-212">
            <button onClick={() => handleNavClick('home')} className={`text-[13px] uppercase tracking-widest font-medium transition-colors duration-300 ${currentPage === 'home' ? activeLinkColor : linkColor}`} data-id="element-213">
              {t.nav.home}
            </button>
            <button onClick={() => handleNavClick('home', 'businesses')} className={`text-[13px] uppercase tracking-widest font-medium transition-colors duration-300 ${linkColor}`} data-id="element-214">
              {t.nav.businesses}
            </button>
            <button onClick={() => handleNavClick('about')} className={`text-[13px] uppercase tracking-widest font-medium transition-colors duration-300 ${currentPage === 'about' ? activeLinkColor : linkColor}`} data-id="element-215">
              {t.nav.about}
            </button>
            <button onClick={() => handleNavClick('contact')} className={`text-[13px] uppercase tracking-widest font-medium transition-colors duration-300 ${currentPage === 'contact' ? activeLinkColor : linkColor}`} data-id="element-216">
              {t.nav.contact}
            </button>

            {/* Language Toggle */}
            <button onClick={toggleLanguage} className={`flex items-center gap-1 text-[12px] font-medium uppercase tracking-wider ${linkColor}`} data-id="element-217">
              <GlobeIcon className="w-3.5 h-3.5" data-id="element-218" />
              {language === 'en' ? 'TH' : 'EN'}
            </button>

            {/* Reserve Button */}
            <a href="https://wa.me/66811770305" target="_blank" rel="noopener noreferrer" className={`px-6 py-2 rounded-sm text-[12px] uppercase tracking-widest font-bold transition-all duration-300 ${reserveBtnClass}`} data-id="element-219">
              {t.nav.reserve}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button className={`md:hidden p-2 -mr-2 transition-colors duration-300 ${textColor}`} onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu" data-id="element-220">
            <MenuIcon className="w-6 h-6" data-id="element-221" />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-222">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-[60] bg-[#1d1d1f] flex flex-col" data-id="element-223">
            <div className="flex items-center justify-between px-6 h-20 border-b border-white/10" data-id="element-224">
              <div className="flex items-center gap-3" data-id="element-225">
                <BVLogo size={44} darkBackground data-id="element-226" />
              </div>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 -mr-2 text-white/80 hover:text-white transition-colors" aria-label="Close menu" data-id="element-227">
                <XIcon className="w-6 h-6" data-id="element-228" />
              </button>
            </div>

            <div className="flex-1 flex flex-col justify-center px-8 gap-8" data-id="element-229">
              <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} onClick={() => handleNavClick('home')} className={`font-display text-4xl font-medium text-left ${currentPage === 'home' ? 'text-[#c9a961]' : 'text-white'}`} data-id="element-230">
                {t.nav.home}
              </motion.button>
              <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.15
          }} onClick={() => handleNavClick('home', 'businesses')} className="font-display text-4xl font-medium text-left text-white/80 hover:text-white" data-id="element-231">
                {t.nav.businesses}
              </motion.button>
              <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} onClick={() => handleNavClick('about')} className={`font-display text-4xl font-medium text-left ${currentPage === 'about' ? 'text-[#c9a961]' : 'text-white'}`} data-id="element-232">
                {t.nav.about}
              </motion.button>
              <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} onClick={() => handleNavClick('contact')} className={`font-display text-4xl font-medium text-left ${currentPage === 'contact' ? 'text-[#c9a961]' : 'text-white'}`} data-id="element-233">
                {t.nav.contact}
              </motion.button>

              <div className="mt-8 flex flex-col gap-6" data-id="element-234">
                <button onClick={toggleLanguage} className="flex items-center gap-2 text-white/80 text-[14px] uppercase tracking-widest" data-id="element-235">
                  <GlobeIcon className="w-4 h-4" data-id="element-236" />
                  Switch to {language === 'en' ? 'Thai' : 'English'}
                </button>

                <a href="https://wa.me/66811770305" target="_blank" rel="noopener noreferrer" className="px-6 py-4 bg-[#c9a961] text-black text-center text-[14px] uppercase tracking-widest font-bold rounded-sm" data-id="element-237">
                  {t.nav.reserve}
                </a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}